import React, { useState, useMemo } from "react";
import { useQuery } from "react-query";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import {
  PageWrapper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  StretchPanel,
  TableHeadCell,
  NoResults,
  PanelSlider,
  Button,
  Select,
  Spinner,
} from "@ubisend/pulse-components";
import { AnimatePresence } from "@ubisend/framer-motion";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  Tooltip,
  BarElement,
} from "chart.js";
import { UserDetails } from "./index";
import { refetchReportMetric } from "../api/index";

dayjs.extend(relativeTime);
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);

const ReportMetric = ({ match: { params } }) => {
  const [showUsers, setShowUsers] = useState(false);

  const currentMonth = dayjs().format("MMMM");
  const currentYear = dayjs().year();

  const [selectedYear, setSelectedYear] = useState({
    label: currentYear,
    value: currentYear,
  });
  const [month, setMonth] = useState({
    label: currentMonth,
    value: currentMonth,
  });

  // eslint-disable-next-line
  const [selectedClients, setSelectedClients] = useState([]);
  const [isRefetching, setIsRefetching] = useState(false);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const metricKeys = [
    { key: "conversations-triggered", label: "Conversations Triggered" },
    { key: "faqs-triggered", label: "FAQs Triggered" },
    { key: "generative-ai-triggered", label: "AI Triggered" },
    { key: "subscribers-created", label: "Subscribers Created" },
    { key: "active-subscribers", label: "Active Subscribers" },
    { key: "total-live-chats", label: "Live Chats" },
    { key: "active-live-chats", label: "Active Live Chats" },
  ];

  const { data, isLoading, refetch } = useQuery(
    ["reports", params.id],
    () =>
      refetchReportMetric({
        clientId: params.id,
        params: {
          year: selectedYear.value ?? selectedYear,
          month: month.value ?? month,
          clients: selectedClients,
        },
      }),
    {
      enabled: true,
      keepPreviousData: true,
    },
  );

  const summaryTotals = useMemo(() => {
    if (!data?.data.clients) return {};
    return {
      conversations: data.data.clients.reduce(
        (sum, c) => sum + (c.metrics["conversations-triggered"] || 0),
        0,
      ),
      faqs: data.data.clients.reduce(
        (sum, c) => sum + (c.metrics["faqs-triggered"] || 0),
        0,
      ),
      generativeai: data.data.clients.reduce(
        (sum, c) => sum + (c.metrics["generative-ai-triggered"] || 0),
        0,
      ),
      subscribers: data.data.clients.reduce(
        (sum, c) => sum + (c.metrics["subscribers-created"] || 0),
        0,
      ),
      activeSubs: data.data.clients.reduce(
        (sum, c) => sum + (c.metrics["active-subscribers"] || 0),
        0,
      ),
    };
  }, [data]);

  const handleData = async () => {
    setIsRefetching(true);
    try {
      await refetch(); // Manually trigger the query
    } catch (error) {
      console.error(error);
    } finally {
      setIsRefetching(false);
    }
  };

  return (
    <PageWrapper header={`${data?.data?.install_name ?? "Reporting Metrics"}`}>
      {/* Filters Section */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          marginBottom: "1.5rem",
        }}
      >
        <Select
          label="Year"
          placeholder="Year"
          value={selectedYear}
          options={[2023, 2024, 2025].map((y) => ({ label: y, value: y }))}
          onChange={(value) => setSelectedYear(value)}
        />
        <Select
          label="Month"
          placeholder="Month"
          value={month}
          options={months.map((m) => ({ label: m, value: m }))}
          onChange={(value) => setMonth(value)}
        />
        <Button onClick={handleData} disabled={isRefetching}>
          {isRefetching ? "Applying..." : "Apply"}
        </Button>
      </div>

      {/* Summary Cards */}
      {isLoading ? (
        <Spinner />
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "1rem",
            marginBottom: "2rem",
          }}
        >
          <SummaryCard
            title="Total Conversations Triggered"
            value={summaryTotals.conversations}
          />
          <SummaryCard
            title="Total FAQs Triggered"
            value={summaryTotals.faqs}
          />
          <SummaryCard
            title="Total Generative AI Triggered"
            value={summaryTotals.generativeai}
          />
          <SummaryCard
            title="Total Subscribers Created"
            value={summaryTotals.subscribers}
          />
          <SummaryCard
            title="Total Active Subscribers"
            value={summaryTotals.activeSubs}
          />
        </div>
      )}

      {/* Bar Chart Section */}
      {!isLoading && data && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
            gap: "2rem",
            marginBottom: "2rem",
          }}
        >
          {metricKeys.map(({ key, label }, i) => {
            const clientColors = {};
            data.data.clients.forEach((client, i) => {
              clientColors[client.name] = `hsl(${(i * 60) % 360}, 70%, 50%)`;
            });

            const chartData = {
              labels: data.data.clients.map((client) => client.name),
              datasets: [
                {
                  data: data.data.clients.map(
                    (client) => client.metrics[key] || 0,
                  ),
                  backgroundColor: data.data.clients.map(
                    (client) => clientColors[client.name],
                  ),
                  borderRadius: 4,
                  barThickness: 20,
                },
              ],
            };

            const chartOptions = {
              responsive: true,
              maintainAspectRatio: false,
              indexAxis: "y",
              plugins: {
                legend: { display: false },
              },
              scales: {
                x: { beginAtZero: true, ticks: { precision: 0, stepSize: 1 } },
                y: { ticks: { autoSkip: false } },
              },
            };

            const chartHeight = data.data.clients.length * 40 + 100;

            return (
              <div
                key={key}
                style={{
                  background: "#fff",
                  padding: "1rem",
                  borderRadius: "8px",
                  height: `${chartHeight}px`,
                }}
              >
                <h4 style={{ marginBottom: "1rem" }}>{label}</h4>
                <div style={{ height: "calc(100% - 5rem)" }}>
                  <Bar data={chartData} options={chartOptions} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Table Section */}
      <StretchPanel>
        <Table loading={isLoading} loadingColumns={6}>
          {!isLoading && data && (
            <>
              {data.data.clients.length === 0 && <NoResults />}
              {data.data.clients.length > 0 && (
                <TableHead>
                  <TableRow>
                    <TableHeadCell>Client</TableHeadCell>
                    <TableHeadCell>Conversations</TableHeadCell>
                    <TableHeadCell>FAQs</TableHeadCell>
                    <TableHeadCell>AI Triggered</TableHeadCell>
                    <TableHeadCell>Subscribers</TableHeadCell>
                    <TableHeadCell>Active Subs</TableHeadCell>
                    <TableHeadCell>Live Chats</TableHeadCell>
                    <TableHeadCell>Active Live Chats</TableHeadCell>
                  </TableRow>
                </TableHead>
              )}
              <TableBody>
                {data.data.clients.map(({ name, metrics }, key) => (
                  <TableRow key={key}>
                    <TableCell>{name}</TableCell>
                    <TableCell>{metrics["conversations-triggered"]}</TableCell>
                    <TableCell>{metrics["faqs-triggered"]}</TableCell>
                    <TableCell>{metrics["generative-ai-triggered"]}</TableCell>
                    <TableCell>{metrics["subscribers-created"]}</TableCell>
                    <TableCell>{metrics["active-subscribers"]}</TableCell>
                    <TableCell>{metrics["total-live-chats"]}</TableCell>
                    <TableCell>{metrics["active-live-chats"]}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </>
          )}
        </Table>
      </StretchPanel>

      {/* Export Buttons
      <div
        style={{
          display: "flex",
          gap: "1rem",
          marginTop: "1.5rem",
          justifyContent: "flex-end",
        }}
      >
        <Button onClick={() => handleExport("summary")}>Export Summary (CSV)</Button>
        <Button onClick={() => handleExport("detailed")}>Export Detailed (CSV)</Button>
      </div>
      */}

      {/* Slide Panel */}
      <AnimatePresence>
        {showUsers && (
          <PanelSlider
            header="View users"
            handleHide={() => setShowUsers(false)}
          >
            <UserDetails users={showUsers} />
          </PanelSlider>
        )}
      </AnimatePresence>
    </PageWrapper>
  );
};

/* ---------------------------
 * Summary Card Component - to be change in next implementation
 * --------------------------- */
const SummaryCard = ({ title, value }) => (
  <div
    style={{
      background: "#fff",
      borderRadius: "8px",
      padding: "1rem 1.5rem",
      textAlign: "center",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    }}
  >
    <div style={{ fontSize: "1.8rem", fontWeight: "bold" }}>{value ?? 0}</div>
    <div style={{ fontSize: "0.9rem", color: "#666" }}>{title}</div>
  </div>
);
export default ReportMetric;
